import { useEffect, useMemo, useRef } from 'preact/hooks'
import { createListenerScope } from '../utils/listener-scope'

interface EventHookOptions {
  enabled?: boolean
  options?: AddEventListenerOptions
  deps?: unknown[]
}

/**
 * Stabilize listener options to prevent effect churn from inline objects.
 * Uses JSON serialization since AddEventListenerOptions is a simple object.
 */
function useStableOptions(options?: AddEventListenerOptions): AddEventListenerOptions | undefined {
  const serialized = options ? JSON.stringify(options) : undefined
  return useMemo(() => (serialized ? JSON.parse(serialized) : undefined), [serialized])
}

export function useWindowEvent<K extends keyof WindowEventMap>(
  event: K,
  handler: (ev: WindowEventMap[K]) => void,
  options: EventHookOptions = {},
): void {
  const { enabled = true, deps = [], options: listenerOptions } = options
  const stableOptions = useStableOptions(listenerOptions)
  const handlerRef = useRef(handler)

  useEffect(() => {
    handlerRef.current = handler
  }, [handler])

  useEffect(() => {
    if (!enabled) return
    const scope = createListenerScope()
    scope.onWindow(event, (ev) => handlerRef.current(ev as WindowEventMap[K]), stableOptions)
    return () => scope.dispose()
  }, [event, enabled, stableOptions, ...deps])
}

export function useDocumentEvent<K extends keyof DocumentEventMap>(
  event: K,
  handler: (ev: DocumentEventMap[K]) => void,
  options: EventHookOptions = {},
): void {
  const { enabled = true, deps = [], options: listenerOptions } = options
  const stableOptions = useStableOptions(listenerOptions)
  const handlerRef = useRef(handler)

  useEffect(() => {
    handlerRef.current = handler
  }, [handler])

  useEffect(() => {
    if (!enabled) return
    const scope = createListenerScope()
    scope.onDocument(event, (ev) => handlerRef.current(ev as DocumentEventMap[K]), stableOptions)
    return () => scope.dispose()
  }, [event, enabled, stableOptions, ...deps])
}

export function useInterval(handler: () => void, ms: number | null, deps: unknown[] = []): void {
  const handlerRef = useRef(handler)

  useEffect(() => {
    handlerRef.current = handler
  }, [handler])

  useEffect(() => {
    if (ms === null) return
    const scope = createListenerScope()
    scope.setInterval(() => handlerRef.current(), ms)
    return () => scope.dispose()
  }, [ms, ...deps])
}

/**
 * Schedule a single RAF callback. Fires once per deps change.
 * Use `useRafLoop` for continuous animation loops.
 */
export function useRafOnce(handler: () => void, deps: unknown[] = []): void {
  const handlerRef = useRef(handler)

  useEffect(() => {
    handlerRef.current = handler
  }, [handler])

  useEffect(() => {
    const scope = createListenerScope()
    scope.requestAnimationFrame(() => handlerRef.current())
    return () => scope.dispose()
  }, deps)
}

/**
 * @deprecated Use `useRafOnce` instead - renamed for clarity.
 * NOTE: This fires a single RAF callback per deps change (not a loop).
 */
export const useRaf = useRafOnce

/**
 * Continuous RAF loop that reschedules itself until unmount or deps change.
 * The handler receives the timestamp from requestAnimationFrame.
 */
export function useRafLoop(handler: (timestamp: number) => void, deps: unknown[] = []): void {
  const handlerRef = useRef(handler)

  useEffect(() => {
    handlerRef.current = handler
  }, [handler])

  useEffect(() => {
    if (typeof window === 'undefined') return

    let rafId: number | null = null
    let cancelled = false

    const loop = (timestamp: number) => {
      if (cancelled) return
      handlerRef.current(timestamp)
      rafId = window.requestAnimationFrame(loop)
    }

    rafId = window.requestAnimationFrame(loop)

    return () => {
      cancelled = true
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId)
      }
    }
  }, deps)
}
